import { Box, Modal, Tab, Text, alpha, styled } from '@ltvco/refresh-lib/theme';

// ─── Search Form ─────────────────────────────────────────────────────────────

export const SearchFormCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
}));

// Pill-style chip button for filter triggers
export const FilterChip = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  borderRadius: '999px',
  border: `1px solid ${
    active ? theme.palette.primary.main : theme.palette.divider
  }`,
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: active ? theme.palette.common.white : theme.palette.text.primary,
  fontSize: '0.8125rem',
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'all 0.15s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: active
      ? theme.palette.primary.dark
      : theme.palette.primary.light + '22',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

FilterChip.defaultProps = {
  type: 'button',
};

// Expandable panel that appears below the chip row
export const FilterExpandPanel = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

// ─── Results Grid ─────────────────────────────────────────────────────────────

export const PatentCardGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const PatentCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[1],
  overflow: 'hidden',
  transition: 'box-shadow 0.2s ease-in-out, transform 0.1s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: theme.shadows[3],
    transform: 'translateY(-1px)',
  },
}));

export const PatentCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)} 0`,
  gap: theme.spacing(1),
}));

export const PatentCardBody = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)} ${theme.spacing(2)}`,
}));

export const PatentCardTitle = styled(Text)({
  fontWeight: 700,
  fontSize: '0.9375rem',
  lineHeight: 1.35,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const PatentCardDetail = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

export const PatentCardDetailLabel = styled(Text)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 700,
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

export const PatentCardDetailValue = styled(Text)({
  fontSize: '0.8125rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const PatentCardStatsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
  marginTop: 'auto',
}));

export const PatentCardStatCell = styled(Box)(({ theme }) => ({
  flex: '1 1 0',
  minWidth: 72,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  gap: theme.spacing(0.5),
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.spacing(1),
  textAlign: 'center',
}));

export const StatCellLabel = styled(Text)({
  fontSize: '0.875rem',
  fontWeight: 700,
  textAlign: 'center',
});

export const StatCellValue = styled(Text)({
  fontSize: '0.875rem',
  textAlign: 'center',
});

// ─── Details Modal ────────────────────────────────────────────────────────────

export const PatentDetailsDialog = styled(Modal)(({ theme }) => ({
  '.MuiPaper-root': {
    width: 'min(860px, calc(100vw - 32px))',
    maxWidth: '860px',
    maxHeight: 'calc(100vh - 32px)',
    margin: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    '.MuiPaper-root': {
      width: 'calc(100vw - 16px)',
      maxHeight: 'calc(100vh - 16px)',
      margin: theme.spacing(1),
    },
  },
}));

export const ModalBody = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(2.5)} ${theme.spacing(2.5)}`,
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

export const ModalAppType = styled(Text)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: theme.spacing(0.5),
}));

export const ModalTitle = styled(Text)({
  fontWeight: 700,
  fontSize: '1.25rem',
  lineHeight: 1.3,
});

export const ModalApplicationNumber = styled(Text)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

// Metadata section (two-column grid)
export const MetadataGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const MetadataItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
}));

export const MetadataLabel = styled(Text)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const MetadataValue = styled(Text)({
  fontSize: '0.9375rem',
  color: 'inherit',
  wordBreak: 'break-word',
});

// Tab-style heading for the Documents section
export const DocumentsTabHeading = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  paddingBottom: theme.spacing(0.75),
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  marginBottom: theme.spacing(2),
}));

// Documents table
export const DocumentsTable = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}));

export const DocumentTableHeaderRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '3fr 1.2fr 1.5fr 1fr',
  backgroundColor: theme.palette.grey[100],
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

export const DocumentTableHeaderCell = styled(Text)(({ theme }) => ({
  fontSize: '0.6875rem',
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}));

export const DocumentTableRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '3fr 1.2fr 1.5fr 1fr',
  padding: `${theme.spacing(1.25)} ${theme.spacing(1.5)}`,
  gap: theme.spacing(1),
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(0.5),
  },
}));

export const DocumentTableCell = styled(Box)({
  minWidth: 0,
  overflow: 'hidden',
});

export const ViewPdfLink = styled('button')(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'right',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

// ─── Shared / Legacy aliases ──────────────────────────────────────────────────

// Keep old names that PatentSearch.tsx still imports
export const SearchModeCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
}));

export const FormCard = SearchFormCard;

export const EmptyStateCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
}));

// PatentSearchParent styled components
export const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 700,
  textTransform: 'none',
  minHeight: '60px',
  marginRight: theme.spacing(3),
  '& .MuiTab-iconWrapper': {
    marginRight: theme.spacing(1.5),
  },
  '& .MuiSvgIcon-root': {
    fontSize: '28px',
  },
}));

// Legacy aliases retained for PatentResults (still used internally)
export const CardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.common.white,
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

export const DetailLabel = styled(Text)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8125rem',
  fontWeight: 600,
  minWidth: '120px',
  flexShrink: 0,
}));

export const DetailValue = styled(Text)({
  fontSize: '0.8125rem',
  wordBreak: 'break-word',
});

export const PatentIconCircle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  color: theme.palette.primary.main,
  flexShrink: 0,
}));

export const PatentCardContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  flex: 1,
  minWidth: 0,
}));

export const PatentCardActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'flex-end',
  flexShrink: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    alignItems: 'flex-start',
    width: 'auto',
  },
}));

export const DocumentCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.default,
}));

export const DownloadButton = styled('button')(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  textDecoration: 'none',
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
  },
}));
